import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { VehicleChecklistMoment, VehicleChecklistValuesEnum } from '../../../services/central-api/dispatch';
import { Modal, TouchableOpacity, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ScrollView } from 'react-native-web-hover';
import FormActions from '../../../components/FormActions';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    unit: AppAgentUnit;
    onConfirm: (list: { [key in VehicleChecklistMoment]?: Partial<VehicleChecklistValues>; }) => void;
}

export default function InspectionModal({ unit, onConfirm, isModalVisible, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const [checkedItems, setCheckedItems] = useState<VehicleChecklistValuesEnum[]>([]);

    const checklistItems = useMemo(() => {
        return unit.externalServiceOrders?.filter(item => item.moment === VehicleChecklistMoment.DURING_SERVICE) ?? [];
    }, [unit, unit.externalServiceOrders]);

    function toggleChecklistItem(value: VehicleChecklistValuesEnum) {
        setCheckedItems(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    }

    function buildChecklist(): { [key in VehicleChecklistMoment]?: Partial<VehicleChecklistValues>; } {
        const values = Object.fromEntries(
            checklistItems.map(item => [item.checklistValue, checkedItems.includes(item.checklistValue)])
        ) as Partial<VehicleChecklistValues>;

        return { [VehicleChecklistMoment.DURING_SERVICE]: values };
    }

    useEffect(() => {
        if (isModalVisible) {
            setCheckedItems(checklistItems.filter(item => item.status).map(item => item.checklistValue));
        }
    }, [isModalVisible]);

    const closeModal = () => {
        onConfirm(buildChecklist());
        setModalVisible(false);
    };

    const cancelModal = () => {
        setModalVisible(false);
    };


    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={cancelModal}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 16 }}>
                        <View style={{ flexDirection: 'column', gap: 8 }}>
                            <MyAppText style={styles.header}>{translate('vehicleChecklist')}</MyAppText>
                            <MyAppText style={styles.vehicleChecklistDescription}>{translate('inspectionDescription')}</MyAppText>
                        </View>
                        <ScrollView>
                            <View style={styles.checklistList}>
                                {checklistItems.map(item => {
                                    const selected = checkedItems.includes(item.checklistValue);
                                    return (
                                        <TouchableOpacity
                                            key={item.id}
                                            style={styles.checklistRow}
                                            onPress={() => toggleChecklistItem(item.checklistValue)}
                                            accessibilityRole='checkbox'
                                            accessibilityState={{ checked: selected }}
                                        >
                                            <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                                                {selected && <FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                                            </View>
                                            <MyAppText style={styles.checklistLabel}>{translate(item.checklistValue)}</MyAppText>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </ScrollView>
                        <FormActions
                            onSubmit={closeModal}
                            onClose={cancelModal}
                            disabled={false}
                            buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                            buttonContentContainerStyle={{ flex: 1 }}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        width: '100%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 24,
        maxHeight: '95%',
        overflow: 'hidden',
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.neutral['500'],
    },
    checklistList: {
        gap: 12,
        paddingLeft: 16
    },
    checklistRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    checklistLabel: {
        fontSize: 16,
        color: theme.colors.labelColor
    },
    vehicleChecklistDescription: {
        color: theme.colors.neutral['400'],
        fontWeight: '400',
        fontSize: 16,
    }
}));
